import { ContractStandardField, EntityTemplateField, isContractStandardField } from "../../entityTemplates/EntityTemplate.types.ts";
import { ContractModel } from "../contract/Contract.types.ts";
import { exhaustiveGuard } from "@common/utilities.ts";

/** The two places a template field's value lives. Tags are relations, so they are not on the model. */
export interface ContractFieldTarget {
    contract: ContractModel;
    selectedTagIds: Array<string>;
}

/**
 * The write half of getFieldValue in ContractValidator.hooks.ts, deliberately the same switch in the same
 * order so a new standard field cannot be added without both halves being updated. Reverting one field to
 * the contract's current value is then two lines and needs no per-field knowledge.
 */
export function setFieldValue(field: EntityTemplateField, target: ContractFieldTarget, value: unknown): void {
    if (field.standardFieldIdentifier && isContractStandardField(field.standardFieldIdentifier)) {
        const standardField = field.standardFieldIdentifier;
        const contract = target.contract;

        switch (standardField) {
            case ContractStandardField.Name:
                contract.name = value as ContractModel["name"];
                return;
            case ContractStandardField.Description:
                contract.description = value as ContractModel["description"];
                return;
            case ContractStandardField.ReferenceNumber:
                contract.referenceNumber = value as ContractModel["referenceNumber"];
                return;
            case ContractStandardField.Note:
                contract.note = value as ContractModel["note"];
                return;
            case ContractStandardField.ContractType:
                contract.contractTypeId = value as ContractModel["contractTypeId"];
                return;
            case ContractStandardField.Status:
                contract.status = value as ContractModel["status"];
                return;
            case ContractStandardField.StartDate:
                contract.startDate = value as ContractModel["startDate"];
                return;
            case ContractStandardField.EndDate:
                contract.endDate = value as ContractModel["endDate"];
                return;
            case ContractStandardField.SignatureDate:
                contract.signatureDate = value as ContractModel["signatureDate"];
                return;
            case ContractStandardField.NotificationOfContractExpiry:
                contract.contractExpiryNotificationInDays = value as ContractModel["contractExpiryNotificationInDays"];
                return;
            case ContractStandardField.Currency:
                contract.currencyCode = value as ContractModel["currencyCode"];
                return;
            case ContractStandardField.TotalAmount:
                contract.totalAmount = value as ContractModel["totalAmount"];
                return;
            case ContractStandardField.FinalisedAmount:
                contract.finalizedAmount = value as ContractModel["finalizedAmount"];
                return;
            case ContractStandardField.PaymentTerm:
                contract.paymentTermId = value as ContractModel["paymentTermId"];
                return;
            case ContractStandardField.BusinessAreas:
                contract.businessAreas = (value as ContractModel["businessAreas"]) ?? [];
                return;
            case ContractStandardField.BusinessAreaAccess:
                contract.contractAccessBusinessAreas = (value as ContractModel["contractAccessBusinessAreas"]) ?? [];
                return;
            case ContractStandardField.LegalEntities:
                contract.groupEntities = (value as ContractModel["groupEntities"]) ?? [];
                return;
            case ContractStandardField.Tags:
                target.selectedTagIds = (value as Array<string>) ?? [];
                return;
            case ContractStandardField.ContractManager:
                contract.contractManagerId = value as ContractModel["contractManagerId"];
                return;
            case ContractStandardField.AdditionalUserAccess:
                contract.additionalAccessUsers = (value as ContractModel["additionalAccessUsers"]) ?? [];
                return;
            case ContractStandardField.ExcludedUsers:
                contract.excludedUsers = (value as ContractModel["excludedUsers"]) ?? [];
                return;
            case ContractStandardField.VisibleToAllUsers:
                contract.visibleToAllUsers = (value as ContractModel["visibleToAllUsers"]) ?? false;
                return;
            case ContractStandardField.CounterParties:
                contract.counterParties = (value as ContractModel["counterParties"]) ?? [];
                return;
            case ContractStandardField.SpecialTerms:
                contract.specialTerms = value as ContractModel["specialTerms"];
                return;
            case ContractStandardField.Documents:
                contract.hasDocuments = (value as ContractModel["hasDocuments"]) ?? false;
                return;
            default:
                exhaustiveGuard(standardField);
                return;
        }
    }

    const existing = target.contract.customFieldValues.find((x) => x.customInputFieldId === field.customInputFieldId);
    if (existing) {
        existing.value = value;
        return;
    }

    if (field.customInputFieldId) {
        target.contract.customFieldValues.push({ customInputFieldId: field.customInputFieldId, value });
    }
}

/**
 * Whether two field values say the same thing, which is what decides if the row offers an arrow at all.
 * Empty is empty however it is spelled, a date is its instant rather than its object identity, and a list
 * of ids is a set: the AI returning the same two business areas in the other order is not a change.
 */
export function isSameFieldValue(current: unknown, proposed: unknown): boolean {
    if (isEmptyFieldValue(current) && isEmptyFieldValue(proposed)) {
        return true;
    }

    if (isEmptyFieldValue(current) || isEmptyFieldValue(proposed)) {
        return false;
    }

    // The contract's dates arrive from the API as strings, while the AI's and the date picker's are Date
    // objects at local noon, so only the day they name can be compared.
    if (current instanceof Date || proposed instanceof Date) {
        return toCalendarDay(current) === toCalendarDay(proposed);
    }

    if (Array.isArray(current) || Array.isArray(proposed)) {
        return Array.isArray(current) && Array.isArray(proposed) && isSameUnorderedList(current, proposed);
    }

    if (typeof current === "object" || typeof proposed === "object") {
        return stableStringify(current) === stableStringify(proposed);
    }

    return current === proposed;
}

// Read the way DotLegalDatePicker reads each kind: a server string by its literal date, a Date by its local
// day. Converting the string to a Date first would shift it a day in any timezone away from UTC.
function toCalendarDay(value: unknown): string {
    if (value instanceof Date) {
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${value.getFullYear()}-${month}-${day}`;
    }

    return String(value).substring(0, 10);
}

export function isEmptyFieldValue(value: unknown): boolean {
    if (value === undefined || value === null) {
        return true;
    }

    if (typeof value === "string") {
        return value.trim().length === 0;
    }

    return Array.isArray(value) && value.length === 0;
}

function isSameUnorderedList(current: Array<unknown>, proposed: Array<unknown>): boolean {
    if (current.length !== proposed.length) {
        return false;
    }

    const remaining = proposed.map(stableStringify);

    return current.every((item) => {
        const position = remaining.indexOf(stableStringify(item));
        if (position < 0) {
            return false;
        }

        remaining.splice(position, 1);
        return true;
    });
}

/** JSON with object keys sorted, so two equal objects built in different orders compare equal. */
function stableStringify(value: unknown): string {
    if (value === null || typeof value !== "object") {
        return JSON.stringify(value) ?? "";
    }

    if (value instanceof Date) {
        return `"${value.getTime()}"`;
    }

    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(",")}]`;
    }

    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, entryValue]) => entryValue !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`);

    return `{${entries.join(",")}}`;
}
